// src/utils/anonymousId.js
//
// ИДЕНТИФИКАТОР БРАУЗЕРА ДЛЯ АНОНИМНОГО ПОСЕТИТЕЛЯ.
//
// 🔑 ЗАЧЕМ ОН НУЖЕН. Согласие на куки спрашивается у человека, который ещё
//    не вошёл. У него НЕТ строки в `users`, поэтому записать согласие в
//    `user_id` физически нечем — нужен хоть какой-то субъект. Им и служит
//    `anonymous_id`, который живёт в localStorage.
//
// ⚠️ ЭТО НЕ ИДЕНТИФИКАТОР ЧЕЛОВЕКА. По нему нельзя войти, он не связан с
//    учётной записью и нужен ровно для одного: чтобы сервер мог отличить
//    «этот браузер уже подтверждал согласие» от «этот — впервые».
//    Поэтому он и в localStorage: это ПОДСКАЗКА, а не доказательство.
//
// ⚠️ ПОЧЕМУ ВЫНЕСЕН В ОБЩИЙ МОДУЛЬ, А НЕ ОСТАВЛЕН В БАННЕРЕ.
//    Функция понадобилась ещё и при регистрации (`AuthPage` фиксирует
//    согласие на обработку ПДн). Две КОПИИ с одним ключом разошлись бы при
//    первой же правке: одна поменяла бы ключ, другая нет — и сервер увидел бы
//    ДВУХ разных анонимов у одного браузера. Здесь ключ объявлен ОДИН раз.
const ANON_KEY = 'studio_anonymous_id';

/**
 * Возвращает (создавая при необходимости) идентификатор браузера.
 *
 * ⚠️ `crypto.randomUUID` есть не во всех браузерах (и только в защищённом
 *    контексте). Фоллбек обязателен: без него согласие нельзя было бы дать
 *    ВООБЩЕ в браузерах без поддержки.
 *
 * @returns {string|null} UUID или null, если получить не удалось.
 *   ⚠️ null — честный ответ: сервер в этом случае попросит авторизоваться,
 *   а не запишет согласие «в никуда».
 */
export function getAnonymousId() {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });

    localStorage.setItem(ANON_KEY, id);
    return id;
  } catch {
    // ⚠️ Приватный режим может запрещать запись. Идентификатор всё равно
    //    вернём — иначе согласие нельзя зафиксировать. Он будет меняться
    //    между загрузками, но у авторизованного согласие и так привязано
    //    к человеку на сервере.
    try {
      return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : null;
    } catch {
      return null;
    }
  }
}

export { ANON_KEY };
